import {
  Button,
  Grid,
  GridCell,
  Icon,
  Input,
  PageSection,
  SegmentedControl,
  SegmentedControlItem,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { FC, useEffect, useState } from 'react';

import { SettingsSectionLoader } from '../../components/settings-section-loader';
import { useLoadingState, useProStudioViewerRole } from '../../hooks';
import { getEnvDotStyle } from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';

type Env = 'live' | 'sandbox';

export const AdvancedSettings: FC = () => {
  const [env, setEnv] = useState<Env>('sandbox');
  const [timeout, setTimeoutValue] = useState('');
  const [attempts, setAttempts] = useState('');

  const { errorHandling, liveAgentPypes } = useProStudioCtxSelector((ctx) => ({
    errorHandling: ctx.errorHandling,
    liveAgentPypes: ctx.liveAgentPypes,
  }));
  const isViewer = useProStudioViewerRole();

  // Load once. See live-agent-platforms.tsx for why this is gated on
  // useLoadingState() too, not just hasLoaded.
  const isPageLoading = useLoadingState();
  // Gate on the pypes as well as the values. `loadErrorHandling` sets both, but
  // `invalidateChannelDerivedCaches` nulls only `liveAgentPypes` — so keying off
  // `errorHandling` alone left the page "loaded" with no pype list after any
  // channel create/delete/rename, and Update had nothing to apply to.
  const hasLoaded = errorHandling != null && liveAgentPypes != null;
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadErrorHandling' });
    }
  }, [isPageLoading, hasLoaded]);

  // Sync the form to the loaded values whenever they or the env change.
  useEffect(() => {
    const values = errorHandling?.[env];
    if (values) {
      setTimeoutValue(String(values.timeout));
      setAttempts(String(values.attempts));
    }
  }, [errorHandling, env]);

  // Both values are written to EVERY connection in the environment, so a
  // negative timeout or zero attempts would break the retry behaviour project-
  // wide. `Number('-5') || 0` happily produced -5 before this.
  const isPositiveInteger = (raw: string): boolean => {
    const parsed = Number(raw);
    return raw.trim() !== '' && Number.isInteger(parsed) && parsed > 0;
  };

  const timeoutValid = isPositiveInteger(timeout);
  const attemptsValid = isPositiveInteger(attempts);
  const canUpdate = hasLoaded && timeoutValid && attemptsValid;

  // The form starts empty and only fills in once `loadErrorHandling` resolves, so
  // validity alone would paint both fields red on arrival — an error state for
  // values the user has neither seen nor typed. The inputs are hidden behind the
  // loader until then; this keeps them honest if the slice is ever invalidated
  // while the page stays mounted (`invalidateChannelDerivedCaches` nulls
  // `liveAgentPypes`, which `hasLoaded` also gates on).
  const showValidation = hasLoaded;

  const handleUpdate = () => {
    if (!canUpdate) return;

    sendProStudioEvent({
      type: 'updateErrorHandling',
      payload: {
        timeout: Number(timeout),
        attempts: Number(attempts),
        env,
      },
    });
  };

  return (
    <PageSection size="small" test-id="ps-advanced-settings-section">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <TextTitle size="small">Error Handling</TextTitle>

        <Stack alignItems="center" gutter="medium" nowrap>
          <SegmentedControl
            test-id="ps-advanced-settings-env"
            fitContent
            value={env}
            onChange={(e) => setEnv(e.detail.value as Env)}
          >
            <SegmentedControlItem value="sandbox">
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', env === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem value="live">
              <span
                slot="prefix"
                style={getEnvDotStyle('live', env === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>

          <Button
            test-id="ps-advanced-settings-update"
            size="large"
            disabled={isViewer || !canUpdate}
            onClick={handleUpdate}
          >
            Update
          </Button>
        </Stack>
      </Stack>

      <Spacer size="xlarge" />

      {!hasLoaded ? (
        <SettingsSectionLoader testId="ps-advanced-settings-loading" />
      ) : (
        <Grid>
          <GridCell xsmall="12" medium="6">
            <Stack alignItems="center" gutter="2xsmall" nowrap>
              <TextBody size="small">Timeout (seconds)</TextBody>
              <Tooltip placement="top">
                <Icon name="info" color="gray" size="xsmall" />
                <TextBody slot="content" size="small">
                  Maximum time allowed to process a response to the user. If
                  exceeded, the Solution automatically retries from the last
                  user input.
                </TextBody>
              </Tooltip>
            </Stack>
            <Spacer size="2xsmall" />
            <Input
              test-id="ps-advanced-settings-timeout"
              variant="outlined"
              type="number"
              min="1"
              disabled={isViewer}
              hasError={showValidation && !timeoutValid}
              helpText="Whole seconds, greater than 0"
              value={timeout}
              onChange={(e) =>
                setTimeoutValue((e.target as HTMLInputElement).value)
              }
              onInput={(e) =>
                setTimeoutValue((e.target as HTMLInputElement).value)
              }
              name="timeout"
            />
          </GridCell>

          <GridCell xsmall="12" medium="6">
            <Stack alignItems="center" gutter="2xsmall" nowrap>
              <TextBody size="small">Total Attempts</TextBody>
              <Tooltip placement="top">
                <Icon name="info" color="gray" size="xsmall" />
                <TextBody slot="content" size="small">
                  The number of times the Solution will attempt to process a
                  response to the user in case of an error or timeout.
                </TextBody>
              </Tooltip>
            </Stack>
            <Spacer size="2xsmall" />
            <Input
              test-id="ps-advanced-settings-attempts"
              variant="outlined"
              type="number"
              min="1"
              disabled={isViewer}
              hasError={showValidation && !attemptsValid}
              helpText="Whole number, 1 or more"
              value={attempts}
              onChange={(e) =>
                setAttempts((e.target as HTMLInputElement).value)
              }
              onInput={(e) => setAttempts((e.target as HTMLInputElement).value)}
              name="attempts"
            />
          </GridCell>
        </Grid>
      )}
    </PageSection>
  );
};
